import React, { useState, useEffect } from "react";
import { generateDispatchGuidePDF } from "../../utils/pdf/DispacheGuidesPDF/generateDispacheGuidesPDF";
import { formatDecimal, formatDate, formatText } from "../../utils/formatters";

// (2026-05-15) Rediseño Sirius. Panel/header/footer/botones migrados al
// patrón estándar y secciones en tarjetas `Item`. La lógica (viewMode,
// PDF, gating de rol) se conserva idéntica.
// (2026-05-21) Refactor:
//   - "Información de la factura" ahora muestra los datos del CLIENTE
//     (afiliada/receptor), no del emisor. El bug anterior mostraba
//     data.empresa.rif / .nombre / .telefono / .email cuando esos campos
//     pertenecen a otra entidad. El modelo expone `data.cliente` (ver
//     GuiaDespachoSchema.get_cliente_final) con rif, nombre, telefono,
//     email y direccion.
//   - Se añade tabla de items (data.items[]) con descripcion, cantidad,
//     peso unit + total, capacidad, volumen unit + total y unidad de
//     medida — antes solo era visible dentro del PDF.
//   - Se añade seccion "Datos formales de la guia" si el backend retorna
//     numero_guia o numero_control_guia (campos opcionales v7.0).
// (2026-07-03) SENIAT Fase 4 (Guía tipo 04): se muestran en solo-lectura
//   los campos nuevos si el API los retorna (motivo por Catálogo 13,
//   descripción del servicio, catálogos 14/15/16 del producto, datos del
//   conductor/vehículo/transportista y serial_seniat como N° Control
//   SENIAT de la guía). Regla: solo se renderiza lo que venga poblado.

// (2026-07-03) SENIAT Fase 4: textos de los catálogos 13/14/15/16
// (GGTIC.GIT.00.02 v1.4) para traducir los códigos en modo lectura.
const CAT13_MOTIVO_TRASLADO = {
  X1: "Reparación o perfeccionamiento",
  X2: "Traslado entre depósitos propios",
  X3: "Otras causas (especifique)",
};
const CAT14_TIPO_PRODUCTO = {
  AL: "Alcohol",
  CI: "Cigarrillos",
};
const CAT15_ORIGEN_PRODUCTO = {
  NA: "Nacional",
  IM: "Importado",
  NI: "Nacional e importado",
};
const CAT16_DESTINO_PRODUCTO = {
  TF: "Tierra firme",
  RE: "Régimen especial",
};
// Muestra "CODIGO — Texto" (o solo el código si no está en el catálogo).
const conCatalogo = (codigo, catalogo) =>
  codigo ? (catalogo[codigo] ? `${codigo} — ${catalogo[codigo]}` : codigo) : "";

const ModalDispatchGuide = ({ data, onClose }) => {
  const [viewMode, setViewMode] = useState("detalle");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    if (!data) {
      setViewMode("detalle");
      setPdfUrl(null);
    }
  }, [data]);

  const handleViewPDF = async () => {
    try {
      setLoadingPdf(true);

      let url;
      url = await generateDispatchGuidePDF(data.id, "view");
      setPdfUrl(url);
      setViewMode("pdf");

    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPdf(false);
    }
  };
  if (!data) return null;
  const authData = localStorage.getItem("authData");
  const rol = authData ? JSON.parse(authData)["rol"] : "";

  const items = Array.isArray(data.items) ? data.items : [];

  // Totales acumulados de la guia.
  const totales = items.reduce((acc, it) => {
    const cant = Number(it.cantidad) || 0;
    const masa = Number(it.masa_kg) || 0;
    const vol  = Number(it.volumen) || 0;
    acc.cantidad += cant;
    acc.peso     += cant * masa;
    acc.volumen  += cant * vol;
    return acc;
  }, { cantidad: 0, peso: 0, volumen: 0 });

  const Item = ({ label, children, full }) => (
    <div className={full ? "w-full" : "w-full sm:w-1/2 lg:w-1/3"}>
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4 h-full">
        <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">{label}</h3>
        <div className="text-sm font-medium text-ink-900 break-words">{children}</div>
      </div>
    </div>
  );

  const SectionHeader = ({ children }) => (
    <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
      {children}
    </h3>
  );

  // Datos formales opcionales (v7.0): solo se renderiza la seccion si
  // alguno de estos campos viene poblado.
  // (2026-07-03) SENIAT Fase 4: se suma serial_seniat (N° Control SENIAT
  // de la guía) si el API lo retorna.
  const tieneDatosFormales = !!(
    data.numero_guia || data.numero_control_guia ||
    data.providencia_nomenclatura || data.providencia_fecha ||
    data.serial_seniat
  );

  // (2026-07-03) SENIAT Fase 4: la seccion de producto (catálogos 14/15/16)
  // solo se renderiza si alguno de los tres códigos viene poblado.
  const tieneDatosProducto = !!(
    data.tipo_producto || data.origen_producto || data.destino_producto
  );

  // (2026-05-22) El modal mantiene un tamaño FIJO en ambos modos
  // (detalle / PDF), igual que ModalInvoices: max-w-[1200px] +
  // max-h-[90vh]. Antes el modo PDF se expandia a 95vw y chocaba con
  // el menu lateral. El visor PDF va en un contenedor de altura fija.
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-[1200px] max-h-[90vh] flex flex-col overflow-hidden">

        {/* HEADER */}
        <div className="px-6 md:px-8 py-5 border-b border-slate-200 bg-white">
          <div className="flex justify-between items-center gap-3">
            <h6 className="text-lg md:text-xl font-display font-bold text-ink-900">
              Guía de despacho - Nº de control: {data.factura?.numero_control || "—"}
            </h6>

            <div className="flex gap-2">
              <button
                className="px-4 py-1.5 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white text-sm font-semibold rounded-lg transition"
                onClick={handleViewPDF}
              >
                Ver PDF
              </button>

              {viewMode === "pdf" && (
                <button
                  className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-ink-700 text-sm font-semibold rounded-lg transition"
                  onClick={() => setViewMode("detalle")}
                >
                  Volver
                </button>
              )}
            </div>
          </div>
        </div>

        {viewMode === "detalle" && (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-8 py-6">
            {rol === 'admin' && (
              <>
                <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mb-3">
                  Datos del Emisor
                </h3>
                <div className="flex flex-wrap gap-3 mb-2">
                  <Item label="R.I.F">{formatText(data.empresa?.rif)}</Item>
                  <Item label="Razón social">{formatText(data.empresa?.nombre_empresa)}</Item>
                </div>
              </>
            )}

            <SectionHeader>Datos del cliente (receptor)</SectionHeader>
            <div className="flex flex-wrap gap-3">
              <Item label="R.I.F">{formatText(data.cliente?.rif)}</Item>
              <Item label="Razón social">{formatText(data.cliente?.nombre)}</Item>
              <Item label="Teléfono">{formatText(data.cliente?.telefono)}</Item>
              <Item label="Correo electrónico">{formatText(data.cliente?.email)}</Item>
              <Item label="Dirección" full>{formatText(data.cliente?.direccion)}</Item>
            </div>

            <SectionHeader>Información de la factura asociada</SectionHeader>
            <div className="flex flex-wrap gap-3">
              <Item label="Número de control">{formatText(data.factura?.numero_control)}</Item>
              <Item label="Fecha de emisión">{formatDate(data.factura?.fecha_emision)}</Item>
            </div>

            <SectionHeader>Datos del traslado</SectionHeader>
            <div className="flex flex-wrap gap-3">
              <Item label="Fecha de salida">{formatDate(data.fecha_salida)}</Item>
              <Item label="Estatus">{formatText(data.estatus)}</Item>
              {/* (2026-07-03) SENIAT Fase 4: motivo por Catálogo 13; el texto
                  libre pasa a ser la especificación del motivo. */}
              {data.motivo_traslado_codigo && (
                <Item label="Motivo del traslado (Catálogo 13)">
                  {conCatalogo(data.motivo_traslado_codigo, CAT13_MOTIVO_TRASLADO)}
                </Item>
              )}
              <Item label={data.motivo_traslado_codigo ? "Especificación del motivo" : "Motivo del traslado"}>
                {formatText(data.motivo_traslado)}
              </Item>
              {data.descripcion_servicio && (
                <Item label="Descripción del servicio" full>{formatText(data.descripcion_servicio)}</Item>
              )}
              <Item label="Ubicación de origen" full>{formatText(data.origen)}</Item>
              <Item label="Dirección de destino" full>{formatText(data.destino)}</Item>
            </div>

            {/* (2026-07-03) SENIAT Fase 4: producto (catálogos 14/15/16),
                solo si algún código viene poblado. */}
            {tieneDatosProducto && (
              <>
                <SectionHeader>Datos SENIAT del producto</SectionHeader>
                <div className="flex flex-wrap gap-3">
                  {data.tipo_producto && (
                    <Item label="Tipo de producto (Catálogo 14)">
                      {conCatalogo(data.tipo_producto, CAT14_TIPO_PRODUCTO)}
                    </Item>
                  )}
                  {data.origen_producto && (
                    <Item label="Origen del producto (Catálogo 15)">
                      {conCatalogo(data.origen_producto, CAT15_ORIGEN_PRODUCTO)}
                    </Item>
                  )}
                  {data.destino_producto && (
                    <Item label="Destino del producto (Catálogo 16)">
                      {conCatalogo(data.destino_producto, CAT16_DESTINO_PRODUCTO)}
                    </Item>
                  )}
                </div>
              </>
            )}

            <SectionHeader>Unidad de transporte</SectionHeader>
            <div className="flex flex-wrap gap-3">
              <Item label="Transportista">{formatText(data.transportista)}</Item>
              <Item label="Chofer">{formatText(data.chofer)}</Item>
              <Item label="Placa">{formatText(data.placa)}</Item>
              {/* (2026-07-03) SENIAT Fase 4: datos del conductor, vehículo y
                  transportista (tabla 28) en solo-lectura si vienen poblados. */}
              {data.conductor_identificacion && (
                <Item label="Identificación del conductor">{formatText(data.conductor_identificacion)}</Item>
              )}
              {data.conductor_tipo_licencia && (
                <Item label="Tipo de licencia">{formatText(data.conductor_tipo_licencia)}</Item>
              )}
              {data.conductor_contacto && (
                <Item label="Contacto del conductor">{formatText(data.conductor_contacto)}</Item>
              )}
              {data.vehiculo_tipo && (
                <Item label="Tipo de vehículo">{formatText(data.vehiculo_tipo)}</Item>
              )}
              {data.vehiculo_numero && (
                <Item label="Número de transporte (vehículo)">{formatText(data.vehiculo_numero)}</Item>
              )}
              {data.transportista_rif && (
                <Item label="R.I.F. del transportista">{formatText(data.transportista_rif)}</Item>
              )}
              {data.transportista_domicilio && (
                <Item label="Domicilio fiscal del transportista" full>{formatText(data.transportista_domicilio)}</Item>
              )}
            </div>

            {tieneDatosFormales && (
              <>
                <SectionHeader>Datos formales de la guía</SectionHeader>
                <div className="flex flex-wrap gap-3">
                  {data.numero_guia && (
                    <Item label="Nº de guía">{formatText(data.numero_guia)}</Item>
                  )}
                  {data.numero_control_guia && (
                    <Item label="Nº de control de la guía">{formatText(data.numero_control_guia)}</Item>
                  )}
                  {/* (2026-07-03) SENIAT Fase 4: número de control SENIAT
                      propio de la guía (tipo 04) si el API lo retorna. */}
                  {data.serial_seniat && (
                    <Item label="N° Control SENIAT (Guía)">{formatText(data.serial_seniat)}</Item>
                  )}
                  {data.providencia_nomenclatura && (
                    <Item label="Providencia">{formatText(data.providencia_nomenclatura)}</Item>
                  )}
                  {data.providencia_fecha && (
                    <Item label="Fecha providencia">{formatDate(data.providencia_fecha)}</Item>
                  )}
                </div>
              </>
            )}

            <SectionHeader>Items despachados ({items.length})</SectionHeader>
            {items.length === 0 ? (
              <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-6 text-center text-sm text-ink-500">
                Esta guía no tiene items registrados.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200/70">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-ink-700">
                    <tr>
                      <th className="text-left py-2 px-3">Descripción</th>
                      <th className="text-right py-2 px-3">Cantidad</th>
                      <th className="text-right py-2 px-3">Peso Unit. (Kg)</th>
                      <th className="text-right py-2 px-3">Peso (Kg)</th>
                      <th className="text-right py-2 px-3">Capacidad</th>
                      <th className="text-right py-2 px-3">Volumen Unit. (M³)</th>
                      <th className="text-right py-2 px-3">Volumen (M³)</th>
                      <th className="text-left py-2 px-3">Unidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const cant = Number(it.cantidad) || 0;
                      const masa = Number(it.masa_kg) || 0;
                      const vol  = Number(it.volumen) || 0;
                      const pesoTotal = cant * masa;
                      const volTotal  = cant * vol;
                      return (
                        <tr key={it.id ?? idx} className="border-t hover:bg-slate-50/60">
                          <td className="py-2 px-3 font-medium text-ink-900">{formatText(it.descripcion)}</td>
                          <td className="py-2 px-3 text-right">{formatDecimal(cant)}</td>
                          <td className="py-2 px-3 text-right">{formatDecimal(masa)}</td>
                          <td className="py-2 px-3 text-right font-semibold">{formatDecimal(pesoTotal)}</td>
                          <td className="py-2 px-3 text-right">{formatDecimal(it.capacidad || 0)}</td>
                          <td className="py-2 px-3 text-right">{formatDecimal(vol)}</td>
                          <td className="py-2 px-3 text-right font-semibold">{formatDecimal(volTotal)}</td>
                          <td className="py-2 px-3 text-ink-700">{formatText(it.unidad_medida)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold text-ink-800">
                    <tr className="border-t">
                      <td className="py-2 px-3 text-right" colSpan={1}>Totales</td>
                      <td className="py-2 px-3 text-right">{formatDecimal(totales.cantidad)}</td>
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3 text-right">{formatDecimal(totales.peso)}</td>
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3 text-right">{formatDecimal(totales.volumen)}</td>
                      <td className="py-2 px-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {viewMode === "pdf" && (
          // Altura fija (75vh) en vez de flex-1: con el modal acotado a
          // max-h-[90vh], header+footer (~16vh) + 75vh entra holgado y
          // el iframe siempre tiene una altura definida que llenar.
          <div className="w-full h-[75vh] bg-slate-100">
            {loadingPdf ? (
              <div className="text-center mt-10 text-ink-500">Generando PDF...</div>
            ) : (
              // Fragmento de URL para el visor PDF del navegador:
              //   toolbar=1/0    -> muestra/oculta la toolbar (gating por rol)
              //   navpanes=0     -> oculta el panel lateral de bookmarks
              //   view=FitH      -> fit horizontal (la pagina ocupa el ancho)
              //   zoom=page-width-> mismo efecto, alternativa por compat
              // Estos parametros son interpretados por Chrome/Edge/Firefox
              // PDF viewers; los que no entienden los ignoran.
              <iframe
                src={`${pdfUrl}#toolbar=${rol === 'visor' ? '0' : '1'}&navpanes=0&view=FitH&zoom=page-width`}
                className="block w-full h-full"
                style={{ border: "none" }}
                title="PDF Viewer"
              />
            )}
          </div>
        )}

        {/* FOOTER */}
        <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalDispatchGuide;
